import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  Contact,
  CreateContactInput,
  PaginatedResult,
  UpdateContactInput,
} from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface ContactListParams {
  customerId?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  isActive?: boolean;
}

const contactKeys = {
  all: ['contacts'] as const,
  list: (params: ContactListParams) => ['contacts', 'list', params] as const,
};

/** Contacts for a customer. Pass `enabled: false` (omit customerId) to skip. */
export function useContacts(
  params: ContactListParams,
  enabled = true,
): UseQueryResult<PaginatedResult<Contact>> {
  return useQuery({
    queryKey: contactKeys.list(params),
    enabled: enabled && Boolean(params.customerId),
    queryFn: () =>
      api.get<PaginatedResult<Contact>>(
        `/contacts${buildQuery({
          customerId: params.customerId,
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 200,
          search: params.search,
          isActive: params.isActive,
        })}`,
      ),
  });
}

/**
 * Every contact across all customers (server-side paged/filtered). Unlike
 * {@link useContacts} this does not require a customer, so it powers the Contacts page.
 */
export function useAllContacts(
  params: ContactListParams,
): UseQueryResult<PaginatedResult<Contact>> {
  return useQuery({
    queryKey: contactKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<Contact>>(
        `/contacts${buildQuery({
          customerId: params.customerId,
          page: params.page ?? 1,
          pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
          search: params.search,
          isActive: params.isActive,
        })}`,
      ),
  });
}

export function useCreateContact(): UseMutationResult<Contact, unknown, CreateContactInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContactInput) => api.post<Contact>('/contacts', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export interface UpdateContactArgs {
  id: string;
  input: UpdateContactInput;
}

export function useUpdateContact(): UseMutationResult<Contact, unknown, UpdateContactArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateContactArgs) =>
      api.patch<Contact>(`/contacts/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useDeleteContact(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/contacts/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}
